import type { ZodType } from 'zod';
import { env } from '@web/core';
import { HttpError, RequestError, ValidationError } from '@voll-intelligence/domain';
import type { AuthService } from '../auth/auth_service';
import { trimTrailingSlashes } from '../url';

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions<T> {
  query?: Record<string, string | number | boolean | null | undefined>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  schema?: ZodType<T>;
}

export interface HttpService {
  get: <T>(path: string, opts?: RequestOptions<T>) => Promise<T>;
  post: <T>(path: string, body?: unknown, opts?: RequestOptions<T>) => Promise<T>;
  put: <T>(path: string, body?: unknown, opts?: RequestOptions<T>) => Promise<T>;
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions<T>) => Promise<T>;
  delete: <T>(path: string, opts?: RequestOptions<T>) => Promise<T>;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const contentType = response.headers.get('content-type') ?? '';
  try {
    if (contentType.includes('application/json')) {
      const payload: unknown = await response.json();
      return payload;
    }
    const text = await response.text();
    return text.length > 0 ? text : null;
  } catch {
    throw new HttpError('Invalid response body', response.status);
  }
}

function toDetails(payload: unknown): Record<string, unknown> | undefined {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return payload as Record<string, unknown>;
  }
  if (payload === null || payload === undefined) return undefined;
  return { body: payload };
}

async function parseResponse<T>(response: Response, opts: RequestOptions<T>): Promise<T> {
  const payload = await parseBody(response);

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'message' in payload
        ? String(payload.message)
        : response.statusText || `HTTP ${String(response.status)}`;
    throw new HttpError(message, response.status, toDetails(payload));
  }

  if (opts.schema) {
    const result = opts.schema.safeParse(payload);
    if (!result.success) throw new ValidationError(result.error);
    return result.data;
  }

  return payload as T;
}

export function createHttpService(
  authService: AuthService,
  /**
   * Base URL for this client. Defaults to kanban-api (VITE_KANBAN_API_URL);
   * pass `env.VITE_ACCOUNTS_API_URL` to construct a client that talks
   * to accounts-api instead.
   */
  baseUrl: string = env.VITE_KANBAN_API_URL,
): HttpService {
  function buildUrl(path: string, query?: RequestOptions<unknown>['query']) {
    const base = trimTrailingSlashes(baseUrl);
    const suffix = path.startsWith('/') ? path : `/${path}`;
    const url = new URL(`${base}${suffix}`);
    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value === null || value === undefined) continue;
        url.searchParams.set(key, String(value));
      }
    }
    return url.toString();
  }

  async function request<T>(method: Method, path: string, body: unknown, opts: RequestOptions<T>) {
    try {
      const headers: Record<string, string> = {
        Accept: 'application/json',
        ...opts.headers,
      };

      const hasBody = body !== undefined && body !== null;
      if (hasBody && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }

      const token = await authService.getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(buildUrl(path, opts.query), {
        method,
        headers,
        body: hasBody ? JSON.stringify(body) : undefined,
        signal: opts.signal,
        credentials: 'omit',
      });

      return await parseResponse(response, opts);
    } catch (error: unknown) {
      if (error instanceof RequestError) throw error;
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      const message = error instanceof Error ? error.message : 'Network request failed';
      throw new HttpError(message, 0);
    }
  }

  return {
    get: (path, opts) => request('GET', path, undefined, opts ?? {}),
    post: (path, body, opts) => request('POST', path, body, opts ?? {}),
    put: (path, body, opts) => request('PUT', path, body, opts ?? {}),
    patch: (path, body, opts) => request('PATCH', path, body, opts ?? {}),
    delete: (path, opts) => request('DELETE', path, undefined, opts ?? {}),
  };
}
